import React, { useMemo } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import ShuttleCard from "./ShuttleCard";


const ShuttleList = ({ crewShuttles = [], port, shuttleMode = 'all' }) => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  /* ========= FLATTEN FLIGHT ========= */
  const makeGroupKey = (f) => `${f.flightDate}|${f.flightDesc}|${f.dep}`;

  const { pickups, takeBacks } = useMemo(() => {
    const pickupMap   = new Map();
    const takeBackMap = new Map();

    crewShuttles.forEach(route => {
      route.routeDetails?.forEach(f => {
        const key = makeGroupKey(f);

        /* ===== PICKUP ===== */
        if (shuttleMode !== 'takeback' && f.fullPickup && (!port || f.dep === port)) {
          if (!pickupMap.has(key)) {
            pickupMap.set(key, {
              ...f,
              crewMembers: [...route.crewMembers],
              crewCount: route.crewCount,
            });
          } else {
            const item = pickupMap.get(key);
            item.crewMembers.push(...route.crewMembers);
            item.crewCount += route.crewCount;
          }
        }

        /* ===== TAKE BACK ===== */
        if (shuttleMode !== 'pickup' && f.fullTakeBack && (!port || f.arr === port)) {
          if (!takeBackMap.has(key)) {
            takeBackMap.set(key, {
              ...f,
              crewMembers: [...route.crewMembers],
              crewCount: route.crewCount,
            });
          } else {
            const item = takeBackMap.get(key);
            item.crewMembers.push(...route.crewMembers);
            item.crewCount += route.crewCount;
          }
        }
      });
    });

    const sortByRank = (a, b) =>
      (a.rankPriority ?? 999) - (b.rankPriority ?? 999);

    const pickups = Array.from(pickupMap.values())
      .map(item => ({
        ...item,
        crewMembers: [...item.crewMembers].sort(sortByRank),
      }))
      .sort((a, b) => new Date(a.fullPickup) - new Date(b.fullPickup));

    const takeBacks = Array.from(takeBackMap.values())
      .map(item => ({
        ...item,
        crewMembers: [...item.crewMembers].sort(sortByRank),
      }))
      .sort((a, b) => new Date(a.fullTakeBack) - new Date(b.fullTakeBack));

    return { pickups, takeBacks };
  }, [crewShuttles, port, shuttleMode]);


  return (
    <Box
      display="grid"
      gap={2}
      gridTemplateColumns="repeat(12, minmax(0, 1fr))"
    >
      {/* PICKUP */}
      {shuttleMode !== 'takeback' && (
        <Box gridColumn={isDesktop && shuttleMode === 'all' ? "span 6" : "span 12"}>
          {pickups.length > 0 && (
            <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
              Pick Up
            </Typography>
          )}
          {pickups.map((row, idx) => (
            <ShuttleCard
              key={`${row.flightDesc}-pickup-${idx}`}
              row={{ ...row, no: idx + 1 }}
              type="pickup"
            />
          ))}
        </Box>
      )}

      {/* TAKE BACK */}
      {shuttleMode !== 'pickup' && (
        <Box gridColumn={isDesktop && shuttleMode === 'all' ? "span 6" : "span 12"}>
          {takeBacks.length > 0 && (
            <Typography
              variant="h5"
              color="secondary"
              sx={{ mb: 1 }}
            >
              Take Back
            </Typography>
          )}
          {takeBacks.map((row, idx) => (
            <ShuttleCard
              key={`${row.flightDesc}-takeback-${idx}`}
              row={{ ...row, no: idx + 1 }}
              type="takeBack"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ShuttleList;
